import translator from '@/locale/translator';

/** The password rule and meter reading; the top segment means exactly "the backend will accept this". */

export const MIN_LENGTH = 8;

/** 0 is an empty field (no meter); 4 means the rule is satisfied. */
export type PasswordStrength = 0 | 1 | 2 | 3 | 4;

export interface PasswordRequirements {
  longEnough: boolean;
  hasUppercase: boolean;
  hasNumberOrSymbol: boolean;
}

export function checkPassword(password: string): PasswordRequirements {
  return {
    longEnough: password.length >= MIN_LENGTH,
    hasUppercase: /\p{Lu}/u.test(password),
    // matches the backend's character classes
    hasNumberOrSymbol: /[\d\p{P}\p{S}]/u.test(password),
  };
}

/** Whether the backend will accept this password; the submit gate reads this. */
export function isPasswordAcceptable(password: string): boolean {
  const met = checkPassword(password);

  return met.longEnough && met.hasUppercase && met.hasNumberOrSymbol;
}

export function passwordStrength(password: string): PasswordStrength {
  if (password === '') return 0;

  const met = checkPassword(password);
  const satisfied = Number(met.longEnough) + Number(met.hasUppercase) + Number(met.hasNumberOrSymbol);

  if (!met.longEnough) return 1;

  return (satisfied + 1) as PasswordStrength;
}

/** The meter label plus a hint naming only the next missing requirement. */
export function passwordAdvice(password: string): { label: string; hint: string } {
  const met = checkPassword(password);

  if (password === '') return { label: '', hint: translator.get('registration.password.rule') };

  if (!met.longEnough)
    return {
      label: translator.get('registration.password.too_short'),
      hint: translator.get('registration.password.needs_length'),
    };

  if (!met.hasUppercase)
    return {
      label: translator.get('registration.password.weak'),
      hint: translator.get('registration.password.needs_uppercase'),
    };

  if (!met.hasNumberOrSymbol) {
    return {
      label: translator.get('registration.password.almost'),
      hint: translator.get('registration.password.needs_number'),
    };
  }

  return { label: translator.get('registration.password.strong'), hint: '' };
}
